/**
 * Berechtigungsmodell.
 *
 * Zentrale Regel aus der Spezifikation: Organigramm != Berechtigungssystem.
 *
 * Jedes Recht ("Funktion der App") hat genau eine Stufe:
 *   Kein Zugriff < Ansehen < Bearbeiten < Verwalten
 *
 * Stufen kommen aus den Funktionen einer Person (je Bereich, optional vererbt)
 * oder aus einer vereinsweiten Ausnahme fuer genau diese Person. Eine Ausnahme
 * ersetzt die Stufe aus den Funktionen - nach oben wie nach unten.
 */
export const RESOURCES = [
    'persons',
    'organisation',
    'tasks',
    'calendar',
    'documents',
    // Umfragen starten und auswerten: Bearbeiten eigene, Verwalten alle im Bereich.
    'polls',
    'media',
    // Freigeben und Veroeffentlichen ist bewusst ein eigenes Recht: wer Beitraege
    // schreibt, darf sie nicht zwangslaeufig veroeffentlichen.
    'media_publish',
    'matches',
    'sports_participation',
    'sports_attendance',
    // Trainingsplanung: Uebungen und Trainingseinheiten ("Bearbeiten": eigene Uebungen und Einheiten pflegen).
    'sports_training_plans',
    // Abschliessen der Anwesenheitsliste - danach ist sie ein Ergebnis.
    'sports_attendance_confirm',
    'sports_reports',
    'sports_reasons',
    // Grundinformation zu Sponsoren: sehen alle, damit niemand doppelt anfragt.
    'sponsoring',
    // Sponsoren anfragen - nur fuer festgelegte Funktionen.
    'sponsoring_requests',
    // Vertraege, Leistungen, Notizen und externe Kontakte.
    'sponsoring_contracts',
    // Betraege - getrennt von den Vertraegen, siehe Finanzmodul.
    'finance',
    'permissions',
];
/**
 * Aktionen, mit denen der Servercode Rechte abfragt. Sie werden auf die
 * benoetigte Stufe abgebildet - gespeichert wird nur die Stufe.
 */
export const ACTIONS = ['read', 'create', 'update', 'delete', 'manage'];
export const LEVELS = ['none', 'read', 'edit', 'manage'];
export const LEVEL_RANK = {
    none: 0,
    read: 1,
    edit: 2,
    manage: 3,
};
export const LEVEL_LABELS = {
    none: 'Kein Zugriff',
    read: 'Ansehen',
    edit: 'Bearbeiten',
    manage: 'Verwalten',
};
const ACTION_LEVEL = {
    read: 'read',
    create: 'edit',
    update: 'edit',
    delete: 'manage',
    manage: 'manage',
};
export function requiredLevel(action) {
    return ACTION_LEVEL[action];
}
export function levelAllows(level, action) {
    return LEVEL_RANK[level] >= LEVEL_RANK[requiredLevel(action)];
}
export function maxLevel(a, b) {
    return LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b;
}
export function isLevel(value) {
    return typeof value === 'string' && LEVELS.includes(value);
}
export function isResource(value) {
    return typeof value === 'string' && RESOURCES.includes(value);
}
export const PERMISSION_FEATURES = [
    { key: 'persons', label: 'Personen', group: 'Verein', description: 'Personen und Kontaktdaten', confidential: false },
    { key: 'organisation', label: 'Vereinsstruktur', group: 'Verein', description: 'Bereiche, Mannschaften und Funktionen zuordnen', confidential: false },
    { key: 'tasks', label: 'Aufgaben', group: 'Organisation', description: 'Aufgaben und Vorlagen', confidential: false },
    { key: 'calendar', label: 'Termine', group: 'Organisation', description: 'Vereinskalender', confidential: false },
    { key: 'documents', label: 'Wissen & Dokumente', group: 'Organisation', description: 'Dokumente und Wissensbeiträge', confidential: false },
    { key: 'polls', label: 'Umfragen', group: 'Organisation', description: 'Umfragen starten und auswerten („Bearbeiten“: eigene, „Verwalten“: alle im Bereich)', confidential: false },
    { key: 'media', label: 'Medien', group: 'Öffentlichkeit', description: 'Medienbeiträge erstellen und bearbeiten', confidential: false },
    { key: 'media_publish', label: 'Medien veröffentlichen', group: 'Öffentlichkeit', description: 'Medienbeiträge freigeben und veröffentlichen (ab „Bearbeiten“)', confidential: false },
    { key: 'matches', label: 'Spiele', group: 'Sport', description: 'Spiele und Ergebnisse', confidential: false },
    { key: 'sports_participation', label: 'Sport-Rückmeldungen', group: 'Sport', description: 'Zu- und Absagen zu Trainings und Spielen', confidential: false },
    { key: 'sports_attendance', label: 'Sport-Anwesenheit', group: 'Sport', description: 'Anwesenheit erfassen', confidential: false },
    { key: 'sports_training_plans', label: 'Trainingsplanung', group: 'Sport', description: 'Übungen und Trainingseinheiten ansehen („Bearbeiten“: eigene Übungen und Einheiten pflegen)', confidential: false },
    { key: 'sports_attendance_confirm', label: 'Anwesenheit abschließen', group: 'Sport', description: 'Anwesenheitslisten abschließen und wieder öffnen (ab „Bearbeiten“)', confidential: false },
    { key: 'sports_reports', label: 'Sport-Auswertungen', group: 'Sport', description: 'Auswertungen zu Beteiligung und Anwesenheit', confidential: false },
    { key: 'sports_reasons', label: 'Absage- und Abwesenheitsgründe', group: 'Sport', description: 'Gründe können Gesundheitsangaben enthalten', confidential: true },
    { key: 'sponsoring', label: 'Sponsoren', group: 'Sponsoring', description: 'Wer Sponsor ist, Betreuung im Verein, weitere Anfragen möglich', confidential: false },
    { key: 'sponsoring_requests', label: 'Sponsoren anfragen', group: 'Sponsoring', description: 'Anfragen erfassen („Bearbeiten“: eigene, „Verwalten“: alle)', confidential: false },
    { key: 'sponsoring_contracts', label: 'Sponsoringverträge', group: 'Sponsoring', description: 'Verträge, Leistungen, Notizen und externe Ansprechpartner', confidential: true },
    { key: 'finance', label: 'Finanzen & Beträge', group: 'Finanzen', description: 'Beträge, Budgets und Mitgliedsbeiträge', confidential: true },
    { key: 'permissions', label: 'Zugriffsrechte', group: 'Verwaltung', description: 'Funktionen, Rechte und Ausnahmen pflegen', confidential: true },
];
/** Standardbeschriftungen - fuer Anzeigen ohne geladenen Vereinskatalog. */
export const RESOURCE_LABELS = Object.fromEntries(PERMISSION_FEATURES.map((feature) => [feature.key, feature.label]));
export const ACTION_LABELS = {
    read: 'Ansehen',
    create: 'Anlegen',
    update: 'Bearbeiten',
    delete: 'Löschen',
    manage: 'Verwalten',
};
/** Schluessel der Funktion mit Vollzugriff (Systemverantwortung). */
export const ADMIN_ROLE_KEY = 'systemverantwortlicher';
const levels = (level, resources) => Object.fromEntries(resources.map((resource) => [resource, level]));
/** Jede Funktion sieht, wer Sponsor ist - Vertraege bleiben vertraulich. */
const base = { sponsoring: 'read' };
/** Lesezugriff auf die allgemeinen Vereinsinhalte. */
const readCommon = levels('read', [
    'persons', 'tasks', 'calendar', 'documents', 'sponsoring', 'media', 'organisation', 'matches',
]);
const manageSports = {
    ...levels('manage', ['sports_participation', 'sports_attendance', 'sports_training_plans', 'sports_reports', 'sports_reasons']),
    sports_attendance_confirm: 'edit',
};
const coachSports = {
    sports_participation: 'edit',
    sports_attendance: 'edit',
    sports_training_plans: 'edit',
    sports_attendance_confirm: 'edit',
    sports_reports: 'read',
    sports_reasons: 'edit',
};
export const SYSTEM_ROLES = [
    {
        key: ADMIN_ROLE_KEY,
        name: 'Systemverantwortlicher',
        description: 'Vollzugriff auf die gesamte Vereinsplattform einschließlich Testansicht',
        isAdmin: true,
        levels: levels('manage', RESOURCES),
    },
    {
        key: 'obmann',
        name: 'Obmann',
        description: 'Gesamtverantwortung für den Verein',
        levels: {
            ...levels('manage', [
                'persons', 'organisation', 'tasks', 'calendar', 'documents', 'media', 'matches',
                'sponsoring', 'sponsoring_requests', 'sponsoring_contracts', 'finance', 'permissions', 'polls',
            ]),
            media_publish: 'edit',
        },
    },
    {
        key: 'sektionsleiter',
        name: 'Sektionsleiter',
        description: 'Leitung einer Sektion',
        levels: {
            ...levels('manage', ['persons', 'tasks', 'calendar', 'documents', 'media', 'matches', 'polls']),
            media_publish: 'edit',
            sponsoring: 'read',
            sponsoring_requests: 'edit',
            organisation: 'read',
            ...manageSports,
        },
    },
    {
        key: 'nachwuchsleiter',
        name: 'Nachwuchsleiter',
        description: 'Leitung des Nachwuchsbereichs',
        levels: {
            ...base,
            ...levels('manage', ['persons', 'tasks', 'calendar', 'polls']),
            documents: 'edit',
            ...levels('read', ['media', 'matches', 'organisation']),
            sponsoring_requests: 'edit',
            ...manageSports,
        },
    },
    {
        key: 'mannschaftsleiter',
        name: 'Mannschaftsleiter',
        description: 'Organisation einer Mannschaft',
        levels: {
            ...base,
            ...levels('read', ['persons', 'documents', 'organisation']),
            ...levels('manage', ['tasks', 'calendar']),
            matches: 'edit',
            polls: 'edit',
            sponsoring_requests: 'edit',
            ...coachSports,
        },
    },
    {
        key: 'trainer',
        name: 'Trainer',
        description: 'Sportliche Leitung einer Mannschaft',
        levels: {
            ...base,
            ...levels('read', ['persons', 'documents', 'organisation']),
            ...levels('edit', ['tasks', 'calendar', 'matches', 'polls']),
            sponsoring_requests: 'edit',
            ...coachSports,
        },
    },
    {
        key: 'co_trainer',
        name: 'Co-Trainer',
        description: 'Unterstützung des Trainers',
        levels: {
            ...base,
            ...levels('read', ['persons', 'calendar', 'documents', 'matches', 'organisation']),
            ...levels('edit', ['tasks', 'polls']),
            ...coachSports,
        },
    },
    {
        key: 'kassier',
        name: 'Kassier',
        description: 'Finanzen und Sponsoring',
        levels: {
            ...readCommon,
            ...levels('manage', ['sponsoring', 'sponsoring_requests', 'sponsoring_contracts', 'finance']),
            ...levels('edit', ['documents', 'tasks', 'polls']),
        },
    },
    {
        key: 'schriftfuehrer',
        name: 'Schriftführer',
        description: 'Protokolle und Dokumente',
        levels: {
            ...readCommon,
            documents: 'manage',
            ...levels('edit', ['tasks', 'calendar', 'polls']),
        },
    },
    {
        key: 'medienverantwortlicher',
        name: 'Medienverantwortlicher',
        description: 'Medien und Öffentlichkeitsarbeit',
        levels: {
            ...readCommon,
            media: 'manage',
            media_publish: 'edit',
            ...levels('edit', ['tasks', 'polls']),
        },
    },
    {
        key: 'sponsorverantwortlicher',
        name: 'Sponsorverantwortlicher',
        description: 'Betreuung der Sponsoren',
        levels: {
            ...readCommon,
            ...levels('manage', ['sponsoring', 'sponsoring_requests', 'sponsoring_contracts', 'finance']),
            ...levels('edit', ['tasks', 'polls']),
        },
    },
    {
        key: 'spieler',
        name: 'Spieler / Sportler',
        description: 'Aktives Mitglied einer Mannschaft oder Sektion',
        levels: { ...base, ...levels('read', ['tasks', 'calendar', 'documents', 'matches', 'organisation']) },
    },
    {
        key: 'mitglied',
        name: 'Mitglied',
        description: 'Einfaches Vereinsmitglied',
        levels: { ...base, ...levels('read', ['tasks', 'calendar', 'documents', 'matches', 'organisation']) },
    },
    {
        key: 'helfer',
        name: 'Helfer',
        description: 'Unterstützung bei Veranstaltungen',
        levels: { ...base, tasks: 'edit', calendar: 'read' },
    },
];
//# sourceMappingURL=permissions.js.map